
import React, { useState, useMemo, useEffect } from 'react';
import { Ticket, Employee, TechnicianPerformanceData, DateRangePreset, TicketStatus, PaymentStatus, TicketsByStatusData, TicketsByCategoryData, TicketTrendDataItem, InventoryItem } from '../types';
import { Button } from './Button';

interface ReportsPageProps {
  ticketsWithSla: Ticket[];
  employees: Employee[];
  currentUser: Employee | null;
  inventoryItems: InventoryItem[]; // Added inventoryItems
}

const ChartBarIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A2.625 2.625 0 011.5 18.375V17.25c0-1.01.488-1.85.996-2.355L7.5 10.5m0 0l3-3m-3 3v6.75m0-6.75L4.5 12M12.75 6.75h4.5m-4.5 0a.75.75 0 01.75-.75h3a.75.75 0 01.75.75m-4.5 0V3m4.5 3.75V3m-6.75 9.75h4.5m-4.5 0a.75.75 0 01.75-.75h3a.75.75 0 01.75.75m-4.5 0V12m4.5 3.75V12m-6.75 9.75h4.5m-4.5 0a.75.75 0 01.75-.75h3a.75.75 0 01.75.75m-4.5 0V15m4.5 3.75V15m6.75-9.75h4.5m-4.5 0a.75.75 0 01.75-.75h3a.75.75 0 01.75.75m-4.5 0V3m4.5 3.75V3" />
  </svg>
);

const ArchiveBoxIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-slate-600" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10.5 11.25h3M12 15h.008" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 7.5h16.5M16.5 7.5V5.625c0-1.036-.84-1.875-1.875-1.875h-5.25C8.34 3.75 7.5 4.59 7.5 5.625V7.5" />
  </svg>
);


const formatDuration = (milliseconds?: number): string => {
  if (milliseconds === undefined || milliseconds < 0) return 'N/A';
  if (milliseconds === 0) return 'Instantly';

  let seconds = Math.floor(milliseconds / 1000);
  let minutes = Math.floor(seconds / 60);
  let hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  hours %= 24;
  minutes %= 60;
  seconds %= 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (parts.length === 0 && seconds > 0) parts.push(`${seconds}s`);
  if (parts.length === 0) return '<1m'; // Or handle very short durations

  return parts.join(' ');
};

const getDatesForPreset = (preset: DateRangePreset): { startDate: Date, endDate: Date } => {
    const now = new Date();
    let startDate = new Date(now);
    let endDate = new Date(now);

    switch (preset) {
        case 'today':
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
        case 'yesterday':
            startDate.setDate(now.getDate() - 1);
            startDate.setHours(0, 0, 0, 0);
            endDate.setDate(now.getDate() - 1);
            endDate.setHours(23, 59, 59, 999);
            break;
        case 'last7days':
            startDate.setDate(now.getDate() - 6); // Include today
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
        case 'last30days':
            startDate.setDate(now.getDate() - 29); // Include today
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
        case 'thisMonth':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
            endDate.setHours(23, 59, 59, 999);
            break;
        case 'lastMonth':
            startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
            endDate.setHours(23, 59, 59, 999);
            break;
        case 'allTime':
            startDate = new Date(0); // Epoch
            endDate = new Date(8640000000000000); // Max Date
            break;
    }
    return { startDate, endDate };
};


export const ReportsPage: React.FC<ReportsPageProps> = ({ ticketsWithSla, employees, currentUser, inventoryItems }) => {
  const [selectedDateRangePreset, setSelectedDateRangePreset] = useState<DateRangePreset>('last30days');

  const dateRange = useMemo(() => getDatesForPreset(selectedDateRangePreset), [selectedDateRangePreset]);

  const filteredTicketsForCreationDate = useMemo(() => {
    if (selectedDateRangePreset === 'allTime') return ticketsWithSla;
    return ticketsWithSla.filter(ticket => {
        const createdAt = new Date(ticket.createdAt);
        return createdAt >= dateRange.startDate && createdAt <= dateRange.endDate;
    });
  }, [ticketsWithSla, dateRange, selectedDateRangePreset]);

  const technicianPerformanceData = useMemo((): TechnicianPerformanceData[] => {
    return employees
      .filter(emp => emp.role.toLowerCase().includes('technician') && emp.status === 'Active')
      .map(technician => {
        const assignedTickets = filteredTicketsForCreationDate.filter(t => t.assignedTo === technician.name);
        const closedTickets = assignedTickets.filter(t => t.status === TicketStatus.COMPLETED);
        
        let totalResolutionTimeMs = 0;
        closedTickets.forEach(t => {
          const resolutionTime = new Date(t.updatedAt).getTime() - new Date(t.createdAt).getTime();
          totalResolutionTimeMs += resolutionTime;
        });
        const avgResolutionTimeMs = closedTickets.length > 0 ? totalResolutionTimeMs / closedTickets.length : undefined;

        return {
          technicianName: technician.name,
          totalAssigned: assignedTickets.length,
          ticketsClosed: closedTickets.length,
          avgResolutionTimeMs: avgResolutionTimeMs,
        };
      })
      .sort((a, b) => b.ticketsClosed - a.ticketsClosed || (a.avgResolutionTimeMs ?? Infinity) - (b.avgResolutionTimeMs ?? Infinity));
  }, [employees, filteredTicketsForCreationDate]);

  // Ticket Volume & Trends Data
  const ticketsByStatus = useMemo((): TicketsByStatusData[] => {
    const statusCounts: Record<TicketStatus, number> = Object.values(TicketStatus).reduce((acc, status) => ({ ...acc, [status]: 0 }), {} as Record<TicketStatus, number>);
    filteredTicketsForCreationDate.forEach(ticket => {
      statusCounts[ticket.status]++;
    });
    return Object.entries(statusCounts).map(([status, count]) => ({
      status: status as TicketStatus,
      count,
      // Simple color mapping, could be more sophisticated
      colorClass: status === TicketStatus.NEW ? 'bg-sky-500' : 
                  status === TicketStatus.IN_PROGRESS ? 'bg-amber-500' :
                  status === TicketStatus.COMPLETED ? 'bg-emerald-500' : 'bg-slate-400',
    })).filter(item => item.count > 0).sort((a,b) => b.count - a.count);
  }, [filteredTicketsForCreationDate]);

  const ticketsByCategory = useMemo((): TicketsByCategoryData[] => {
    const categoryCounts: { [key: string]: number } = {};
    filteredTicketsForCreationDate.forEach(ticket => {
      const category = ticket.category || 'Uncategorized';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });
    return Object.entries(categoryCounts).map(([category, count]) => ({ category, count })).sort((a,b) => b.count - a.count);
  }, [filteredTicketsForCreationDate]);

  const ticketCreationTrend = useMemo((): TicketTrendDataItem[] => {
    const trendData: { [key: string]: number } = {};
    const diffDays = (dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 3600 * 24);

    filteredTicketsForCreationDate.forEach(ticket => {
      const createdAt = new Date(ticket.createdAt);
      let periodKey: string;
      if (diffDays <= 31) { // Daily for up to a month
        periodKey = createdAt.toISOString().split('T')[0];
      } else if (diffDays <= 90) { // Weekly for up to 3 months
        const weekStart = new Date(createdAt);
        weekStart.setDate(createdAt.getDate() - createdAt.getDay() + (createdAt.getDay() === 0 ? -6 : 1)); // Monday as start of week
        periodKey = `Week of ${weekStart.toISOString().split('T')[0]}`;
      } else { // Monthly
        periodKey = createdAt.toLocaleString('default', { month: 'short', year: 'numeric' });
      }
      trendData[periodKey] = (trendData[periodKey] || 0) + 1;
    });
    return Object.entries(trendData).map(([period, count]) => ({ period, count })).sort((a,b) => new Date(a.period.replace('Week of ', '')).getTime() - new Date(b.period.replace('Week of ', '')).getTime());
  }, [filteredTicketsForCreationDate, dateRange]);
  
  // Revenue & Payments Data
  const paidTicketsInPeriod = useMemo(() => {
    return ticketsWithSla.filter(ticket => {
        if (ticket.paymentStatus !== PaymentStatus.PAID) return false;
        const paymentDate = new Date(ticket.updatedAt); // Assuming updatedAt on COMPLETED tickets is payment date
        return selectedDateRangePreset === 'allTime' || (paymentDate >= dateRange.startDate && paymentDate <= dateRange.endDate);
    });
  }, [ticketsWithSla, dateRange, selectedDateRangePreset]);

  const totalRevenueInPeriod = useMemo(() => {
    return paidTicketsInPeriod.reduce((sum, ticket) => sum + ticket.amountDue, 0);
  }, [paidTicketsInPeriod]);

  const outstandingTicketsList = useMemo(() => {
    return ticketsWithSla.filter(ticket => 
        ticket.paymentStatus === PaymentStatus.UNPAID && 
        ticket.amountDue > 0 &&
        (ticket.status === TicketStatus.READY_FOR_PICKUP || ticket.status === TicketStatus.COMPLETED || ticket.status === TicketStatus.IN_PROGRESS)
    ).sort((a,b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [ticketsWithSla]);

  const totalOutstandingAmount = useMemo(() => {
    return outstandingTicketsList.reduce((sum, ticket) => sum + ticket.amountDue, 0);
  }, [outstandingTicketsList]);
  
  // Inventory Usage Data
  const frequentlyUsedPartsData = useMemo(() => {
    const partsMap = new Map<string, { itemName: string; totalQuantityUsed: number; totalCostOfPartsUsed: number }>();
    filteredTicketsForCreationDate.forEach(ticket => {
      ticket.partsUsed.forEach(part => {
        const existing = partsMap.get(part.inventoryItemId);
        const costForThisUsage = part.quantityUsed * (part.unitCostAtTimeOfUse || 0);
        if (existing) {
          existing.totalQuantityUsed += part.quantityUsed;
          existing.totalCostOfPartsUsed += costForThisUsage;
        } else {
          partsMap.set(part.inventoryItemId, {
            itemName: part.itemName,
            totalQuantityUsed: part.quantityUsed,
            totalCostOfPartsUsed: costForThisUsage,
          });
        }
      });
    });
    return Array.from(partsMap.values()).sort((a, b) => b.totalQuantityUsed - a.totalQuantityUsed);
  }, [filteredTicketsForCreationDate]);

  const partsUsageSummary = useMemo(() => {
    let totalCostOfAllPartsUsed = 0;
    let numberOfTicketsWithParts = 0;
    filteredTicketsForCreationDate.forEach(ticket => {
      if (ticket.partsUsed.length > 0) {
        numberOfTicketsWithParts++;
        ticket.partsUsed.forEach(part => {
          totalCostOfAllPartsUsed += part.quantityUsed * (part.unitCostAtTimeOfUse || 0);
        });
      }
    });
    return {
      totalCostOfAllPartsUsed,
      numberOfTicketsWithParts,
      averageCostOfPartsPerTicketWithParts: numberOfTicketsWithParts > 0 ? totalCostOfAllPartsUsed / numberOfTicketsWithParts : 0,
    };
  }, [filteredTicketsForCreationDate]);

  const LOW_STOCK_THRESHOLD = 5;
  const lowStockItems = useMemo(() => {
    return inventoryItems.filter(item => 
        (item.usageType === 'repair' || item.usageType === 'sale' || item.usageType === 'general') && 
        item.stock < LOW_STOCK_THRESHOLD
    ).sort((a,b) => a.stock - b.stock);
  }, [inventoryItems]);


  const renderSection = (title: string, children: React.ReactNode, icon?: React.ReactNode) => (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-slate-700 mb-4 border-b border-slate-200 pb-2 flex items-center">
        {icon}
        {title}
      </h2>
      {children}
    </div>
  );
  
  const renderTable = (headers: string[], data: (string | number | React.ReactNode)[][], emptyMessage: string = "No data available for this period.") => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            {headers.map((header, idx) => (
              <th key={idx} className={`px-4 py-2 text-left font-medium text-slate-500 ${idx > 0 ? 'text-center' : ''} ${idx === headers.length -1 ? '!text-right' : ''}`}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200">
          {data.length === 0 ? (
            <tr><td colSpan={headers.length} className="px-4 py-3 text-center text-slate-500">{emptyMessage}</td></tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className={`px-4 py-2 whitespace-nowrap text-slate-700 ${cellIndex > 0 ? 'text-center' : ''} ${cellIndex === headers.length -1 ? 'text-right' : ''}`}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );


  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-1">
          <ChartBarIcon />
          <h1 className="text-2xl font-bold text-[var(--color-primary)]">Reports & Analytics</h1>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <p className="text-sm text-slate-600">Analyze performance and trends for your service operations.</p>
            <div>
                <label htmlFor="dateRangeSelect" className="sr-only">Select Date Range</label>
                <select
                    id="dateRangeSelect"
                    value={selectedDateRangePreset}
                    onChange={(e) => setSelectedDateRangePreset(e.target.value as DateRangePreset)}
                    className="px-3 py-2 border border-slate-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-sm"
                >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7days">Last 7 Days</option>
                    <option value="last30days">Last 30 Days</option>
                    <option value="thisMonth">This Month</option>
                    <option value="lastMonth">Last Month</option>
                    <option value="allTime">All Time</option>
                </select>
            </div>
        </div>
      </div>

      {renderSection("Technician Performance", (
        renderTable(
          ["Technician", "Assigned", "Closed", "Avg. Resolution Time"],
          technicianPerformanceData.map(tech => [
            tech.technicianName,
            tech.totalAssigned,
            tech.ticketsClosed,
            formatDuration(tech.avgResolutionTimeMs)
          ]),
          "No technician data for selected period."
        )
      ))}

      {renderSection("Ticket Volume & Trends", (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
                <h3 className="font-semibold text-slate-600 mb-2">Tickets by Status</h3>
                {ticketsByStatus.length > 0 ? (
                    <ul className="space-y-1 text-sm">
                        {ticketsByStatus.map(s => <li key={s.status} className="flex justify-between items-center p-1.5 bg-slate-50 rounded"><span>{s.status}</span> <span className={`font-bold px-1.5 py-0.5 text-xs rounded-full text-white ${s.colorClass}`}>{s.count}</span></li>)}
                    </ul>
                ) : <p className="text-xs text-slate-500">No tickets in this period.</p>}
            </div>
            <div>
                <h3 className="font-semibold text-slate-600 mb-2">Tickets by Category</h3>
                {ticketsByCategory.length > 0 ? (
                    <ul className="space-y-1 text-sm max-h-60 overflow-y-auto">
                        {ticketsByCategory.map(c => <li key={c.category} className="flex justify-between items-center p-1.5 bg-slate-50 rounded"><span>{c.category}</span> <span className="font-bold text-slate-700">{c.count}</span></li>)}
                    </ul>
                ) : <p className="text-xs text-slate-500">No categorized tickets in this period.</p>}
            </div>
            <div>
                <h3 className="font-semibold text-slate-600 mb-2">Ticket Creation Trend</h3>
                 {ticketCreationTrend.length > 0 ? (
                    <ul className="space-y-1 text-sm max-h-60 overflow-y-auto">
                        {ticketCreationTrend.map(t => <li key={t.period} className="flex justify-between items-center p-1.5 bg-slate-50 rounded"><span>{t.period}</span> <span className="font-bold text-slate-700">{t.count}</span></li>)}
                    </ul>
                ) : <p className="text-xs text-slate-500">No ticket creation trend data for this period.</p>}
            </div>
        </div>
      ))}
      
      {renderSection("Revenue & Payments", (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-emerald-50 p-4 rounded-md shadow">
                    <p className="text-sm text-emerald-700 font-medium">Total Revenue (Paid in Period)</p>
                    <p className="text-2xl font-bold text-emerald-600">${totalRevenueInPeriod.toFixed(2)}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-md shadow">
                    <p className="text-sm text-red-700 font-medium">Total Outstanding (All Relevant Tickets)</p>
                    <p className="text-2xl font-bold text-red-600">${totalOutstandingAmount.toFixed(2)}</p>
                </div>
            </div>
            <div>
                <h3 className="font-semibold text-slate-600 mb-2">Paid Tickets ({paidTicketsInPeriod.length}) - Period: {selectedDateRangePreset}</h3>
                <div className="overflow-x-auto max-h-80 text-sm border rounded-md">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-100 sticky top-0">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium text-slate-500">ID</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-500">Customer</th>
                                <th className="px-3 py-2 text-right font-medium text-slate-500">Amount</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-500">Paid Date</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                            {paidTicketsInPeriod.map(t => (
                                <tr key={t.id}>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{t.id}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-700 truncate max-w-xs">{t.customer}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-right text-emerald-600 font-semibold">${t.amountDue.toFixed(2)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{new Date(t.updatedAt).toLocaleDateString()}</td>
                                </tr>
                            ))}
                             {paidTicketsInPeriod.length === 0 && <tr><td colSpan={4} className="px-3 py-3 text-center text-slate-500">No paid tickets in this period.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
            <div>
                <h3 className="font-semibold text-slate-600 mb-2">Outstanding Tickets ({outstandingTicketsList.length})</h3>
                <div className="overflow-x-auto max-h-80 text-sm border rounded-md">
                     <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-100 sticky top-0">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium text-slate-500">ID</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-500">Customer</th>
                                <th className="px-3 py-2 text-right font-medium text-slate-500">Amount Due</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-500">Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                            {outstandingTicketsList.map(t => (
                                <tr key={t.id}>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{t.id}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-700 truncate max-w-xs">{t.customer}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-right text-red-600 font-semibold">${t.amountDue.toFixed(2)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{t.status}</td>
                                </tr>
                            ))}
                            {outstandingTicketsList.length === 0 && <tr><td colSpan={4} className="px-3 py-3 text-center text-slate-500">No outstanding tickets matching criteria.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
      ))}

      {renderSection("Inventory Usage Report", 
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                 <div className="bg-sky-50 p-4 rounded-md shadow">
                    <p className="text-sky-700 font-medium">Total Cost of Parts Used (Period)</p>
                    <p className="text-2xl font-bold text-sky-600">
                        ${partsUsageSummary.totalCostOfAllPartsUsed.toFixed(2)}
                    </p>
                </div>
                <div className="bg-sky-50 p-4 rounded-md shadow">
                    <p className="text-sky-700 font-medium">Avg. Parts Cost / Ticket (with parts)</p>
                    <p className="text-2xl font-bold text-sky-600">
                        ${partsUsageSummary.averageCostOfPartsPerTicketWithParts.toFixed(2)}
                    </p>
                     <p className="text-xs text-slate-500">({partsUsageSummary.numberOfTicketsWithParts} tickets used parts)</p>
                </div>
            </div>

            <div>
                <h3 className="font-semibold text-slate-600 mb-2">Frequently Used Parts (by Quantity)</h3>
                {renderTable(
                    ["Part Name", "Total Quantity Used", "Total Cost of Parts Used"],
                    frequentlyUsedPartsData.map(part => [
                        part.itemName,
                        part.totalQuantityUsed,
                        `$${part.totalCostOfPartsUsed.toFixed(2)}`
                    ]),
                    "No parts usage data for this period."
                )}
            </div>
            <div>
                <h3 className="font-semibold text-slate-600 mb-2">Low Stock Items (Stock &lt; {LOW_STOCK_THRESHOLD})</h3>
                {renderTable(
                    ["Item Name", "SKU", "Current Stock", "Usage Type"],
                    lowStockItems.map(item => [
                        item.name,
                        item.sku || 'N/A',
                        <span key={`stock-${item.id}`} className={`font-bold ${item.stock <= LOW_STOCK_THRESHOLD / 2 ? 'text-red-600' : 'text-amber-600'}`}>{item.stock}</span>,
                        item.usageType || 'N/A'
                    ]),
                    "No items are currently low in stock."
                )}
            </div>
        </div>,
        <ArchiveBoxIcon />
      )}
    </div>
  );
};
